import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import ChatTurnList from "../../components/Chat/ChatList/ChatTurnList";
import ChatWelcome from "../../components/Chat/ChatWelcome/ChatWelcome";
import { ClearChatButton } from "../../components/Chat/ClearChatButton";
import { DownloadConversationButton } from "../../components/Chat/DownloadConversationButton";
import { QuestionInput } from "../../components/Chat/QuestionInput";
import GradientText from "../../components/Common/GradientText";
import documentViewPanelSettings from "../../constants/documentViewPanelSettings";
import { useConversation } from "../../contexts/conversationContext";
import useChatData from "../../hooks/useChatData";
import styles from "./Chat.module.css";
import { useTranslation } from "react-i18next";

import { AnalysisPanel } from "../../components/Chat/AnalysisPanel/AnalysisPanel";
import { downloadConversationApi } from "../../api/endpoints/fileAPI";


const MotionBox = motion(Box);

const { chatVariants, panelVariants, spring } = documentViewPanelSettings();

const Chat = () => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const min768 = useMediaQuery("(min-width:768px)");
    const min1100 = useMediaQuery("(min-width:1100px)");

    // Reference to the end of the chat message stream for auto-scrolling
    const scrollRef = useRef<HTMLDivElement | null>(null);

    const { chatHistory, isAnalysisPanelTabActive, selectedAnswer, hasMessage, sessionId } = useConversation();

    // Use custom hook for chat data management (state, API calls, etc.)
    const { isLoading, makeApiRequest, retry, clearChat } = useChatData();

    // Download conversation state
    const [isDownloading, setIsDownloading] = useState(false);

    const downloadConversation = useCallback(async () => {
        if (!sessionId) return;
        setIsDownloading(true);
        try {
            const blob = await downloadConversationApi(sessionId, i18n.language);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `conversation_${sessionId}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to download conversation:", error);
        } finally {
            setIsDownloading(false);
        }
    }, [sessionId]);

    // Auto-scroll to the bottom of the chat whenever loading state changes
    useEffect(() => scrollRef.current?.scrollIntoView({ behavior: "smooth" }), [isLoading]);

    const onExampleClicked = (example: string) => makeApiRequest(example);

    const shouldShowPanel = chatHistory.length > 0 && isAnalysisPanelTabActive && selectedAnswer;

    return (
        <Box
            sx={{
                height: "100%",
                width: "100%",
                justifyContent: "flex-start",
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
                background: theme.palette.containerBG
            }}
        >
            {/* Commands container with Clear Chat and Download Conversation buttons */}
            {hasMessage && (
                <Box className={styles.commandsContainer}>
                    <DownloadConversationButton onClick={downloadConversation} disabled={!hasMessage || isLoading} isLoading={isDownloading} />
                    <ClearChatButton onClick={isLoading ? () => {} : clearChat} disabled={!hasMessage || isLoading} />
                    <GradientText variant="h1" sx={{ marginTop: "0px", fontSize: min768 ? "1.5rem" : "1.25rem" }}>
                        {t("chat.alexxisTitle")}
                    </GradientText>
                </Box>
            )}

            <Box
                sx={{
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                    overflow: "hidden",
                    gap: 2
                }}
            >
                {/* =================== CHAT COLUMN =================== */}
                {/* ≥1100px: animated width. <1100px: hide when panel active */}
                {(min1100 || (!min1100 && !shouldShowPanel)) && (
                    <MotionBox
                        key="chat-col"
                        initial={false}
                        animate={min1100 ? (shouldShowPanel ? "shrunk" : "full") : undefined}
                        variants={chatVariants}
                        transition={min1100 ? spring : { duration: 0 }}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                            height: "100%",
                            justifyContent: "center",
                            alignItems: "center",
                            paddingInline: 0.25,
                            width: "100%",
                            minWidth: min1100 ? "500px" : "300px"
                        }}
                    >
                        {chatHistory.length === 0 && <ChatWelcome onExampleClicked={onExampleClicked} />}
                        {chatHistory.length > 0 && (
                            <Box
                                className={styles.chatMessageStream}
                                sx={{
                                    "&::-webkit-scrollbar": {
                                        width: "8px",
                                        backgroundColor: "transparent"
                                    },
                                    "&::-webkit-scrollbar-thumb": {
                                        backgroundColor: theme.palette.scrollbarThumb,
                                        borderRadius: "4px",
                                        "&:hover": {
                                            backgroundColor: theme.palette.scrollbarThumb
                                        }
                                    },
                                    "&::-webkit-scrollbar-track": {
                                        backgroundColor: theme.palette.scrollbarTrack,
                                        borderRadius: "4px"
                                    },
                                    // For Firefox
                                    scrollbarWidth: "auto",
                                    scrollbarColor: `${theme.palette.scrollbarThumb} ${theme.palette.scrollbarTrack}`,
                                    paddingInline: 0
                                }}
                            >
                                <ChatTurnList retry={retry} />
                            </Box>
                        )}

                        <Box
                            sx={{
                                display: "flex",
                                position: "sticky",
                                bottom: "20",
                                left: "0",
                                right: "0",
                                width: "90%",
                                minWidth: min1100 ? "500px" : "300px",
                                maxWidth: "1024px",
                                flexDirection: "column",
                                marginInline: "12px",
                                paddingBottom: "16px",
                                alignItems: "center"
                            }}
                        >
                            <QuestionInput
                                clearOnSend
                                placeholder={t("chat.questionInputText")}
                                disabled={isLoading}
                                onSend={question => makeApiRequest(question)}
                            />
                            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", paddingTop: "1rem", gap: 0.5 }}>

                                <Typography sx={{ color: theme.palette.disclaimerText, fontSize: "0.875rem", lineHeight: "1" }}>
                                    {t("common.copyright")}
                                </Typography>
                            </Box>
                        </Box>
                    </MotionBox>
                )}

                {/* =================== ANALYSIS PANEL =================== */}
                {/* ≥1100px: animate open/close with AnimatePresence.
            <1100px: hard swap (no animations). */}
                {min1100 ? (
                    <AnimatePresence mode="sync" initial={false}>
                        {shouldShowPanel && (
                            <MotionBox
                                key="analysis-panel"
                                className={styles.chatAnalysisPanelContainer}
                                sx={{ flexGrow: 1, minWidth: 0 }}
                                variants={panelVariants}
                                initial="closed"
                                animate="open"
                                exit="closed"
                                transition={spring}
                            >
                                <AnalysisPanel />
                            </MotionBox>
                        )}
                    </AnimatePresence>
                ) : (
                    shouldShowPanel && (
                        <Box key="analysis-panel-static" className={styles.chatAnalysisPanelContainer} sx={{ flexGrow: 1 }}>
                            <AnalysisPanel />
                        </Box>
                    )
                )}
            </Box>
        </Box>
    );
};

export default Chat;
